import { Entitlement, Plan, PlanPrice } from "src/apis/subscriptionApi";
import { DiscountControllerOptionFragment } from "../../../components/controllers/DiscountSelect/DiscountSelect-generated.hooks";
import { PaymentMethodControllerOptionFragment } from "../../../components/controllers/UserBillingSelect/UserBillingSelect-generated.hooks";
import { UserSubscriptionSwitchCriteria } from "../../../graphql/graphql-generated.types";
import { getBillingOrSessionCycleOptions } from "../../../utils/billingOrSessionCycleOptions.utils";
import {
  CalendarItemTypeFragment,
  CurrentUserSubscriptionFragment,
} from "../SwitchPlanDrawer-generated.hooks";
import { AppointmentType } from "src/apis/appointmentApi";

export interface SwitchPlanForm {
  newPlan: Plan;
  planPrice?: PlanPrice;
  discount?: DiscountControllerOptionFragment;
  setupFee: number;
  whenToSwitch: UserSubscriptionSwitchCriteria;
  paymentMethod: PaymentMethodControllerOptionFragment;
}

export function getBillingIntervalType(intervalType: string, interval: number) {
  switch (intervalType) {
    case "D":
    case "day":
      if (interval === 1) {
        return "per day";
      }
      return `every ${interval} days`;
    case "W":
    case "week":
      if (interval === 1) {
        return "per week";
      }
      return `every ${interval} weeks`;
    case "M":
    case "month":
      if (interval === 1) {
        return "per month";
      }
      return `every ${interval} months`;
    case "Y":
    case "year":
      if (interval === 1) {
        return "per year";
      }
      return `every ${interval} years`;
    default:
      return "Invalid";
  }
}

export function getUserSubscriptionBillingIntervalName(
  userSubscription: CurrentUserSubscriptionFragment,
) {
  switch (userSubscription.intervalType) {
    case "D":
      return "day";
    case "W":
      return "week";
    case "M":
      return "month";
    case "Y":
      return "year";
    default:
      return "month";
  }
}

export function getPlanNumberOfSessionsText(
  subscriptionPlan: Plan,
) {
  if (subscriptionPlan.sessionsPerPeriod === "Unlimited") {
    return "Unlimited";
  }

  const numberOfSessions = Number(subscriptionPlan.sessionsPerPeriod);
  const sessionText = numberOfSessions === 1 ? "session" : "sessions";
  const period = subscriptionPlan.isCheckinWindowTiedToBillingWindow
    ? subscriptionPlan.intervalType
    : subscriptionPlan.sessionsPeriod;
  const interval = subscriptionPlan.isCheckinWindowTiedToBillingWindow
    ? subscriptionPlan.interval
    : 1;

  const perPeriodText = getBillingOrSessionCycleOptions(period, interval);

  return `${numberOfSessions} ${sessionText} ${perPeriodText}`;
}

export function getCalendarItemTypesRestrictions(
  subscriptionPlan: Plan,
  calendarItemTypes: CalendarItemTypeFragment[],
) {
  if (!subscriptionPlan.hasCalendarItemTypeRestriction) return "All classes";

  return subscriptionPlan.calendarItemTypes
    .map((item) => {
      const calendarItemType = calendarItemTypes.find(
        (type) => type.uuid === item,
      );

      return calendarItemType?.name || "";
    })
    .join(", ");
}

export function getPlanLimitQuantityHelperText(
  subscriptionPlan?: Plan,
) {
  if (!subscriptionPlan || !subscriptionPlan.limitQuantity) return null;

  return `${subscriptionPlan.quantityAvailable} remaining (limited quantity)`;
}

export function getEntitlementDescription(
  entitlement: Entitlement,
  appointmentType?: AppointmentType,
): string {
  if (!entitlement?.entitlementUuid) {
    return "Empty entitlement";
  }
  // Defensive programming in case we have other types passed in the future
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (entitlement?.entitlementType === "APPOINTMENT_TYPE") {
    return appointmentType?.name ?? "Invalid appointment type";
  }
  return "Invalid entitlement";
}
